import type { MouseEvent } from 'react';
import { Suspense, lazy, memo, useMemo } from 'react';
import DefaultIcon from '@mui/icons-material/ImageNotSupported';
import type { SxProps, Theme } from '@mui/material';
import { DynamicIconSkeleton } from '@/components/ui/icons/DynamicIcon/components/DynamicIconSkeleton';
import { documentFormatIconMap, iconMap, stateFlagIconMap } from '@/constants/iconMap';
import TypographyService from '@/services/TypographyService/TypographyService';
import { StyledDynamicIconBox, StyledDynamicIconBoxProps } from './DynamicIcon.styles';

const lazyIconsCache = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

const getLazyIcon = (folder: string, iconSource: string) => {
  const key = `${folder}/${iconSource}`;

  if (!lazyIconsCache.has(key)) {
    const LazyIcon = lazy(() => import(`@/assets/${folder}/${iconSource}.svg?react`));
    lazyIconsCache.set(key, LazyIcon);
  }

  return lazyIconsCache.get(key);
};

type DynamicIconProps = StyledDynamicIconBoxProps & {
  code?: null | string; // we could get null from BE
  width?: number;
  'data-testid'?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  sx?: SxProps<Theme>;
  className?: string;
  color?: string;
};

const iconMapByFolder = {
  icons: iconMap,
  stateFlags: stateFlagIconMap,
  documentFormats: documentFormatIconMap,
} as const;

export const DynamicIcon = memo(
  ({
    code,
    width = 20,
    height = 20,
    fill,
    stroke,
    color,
    sx = {},
    onClick,
    withBackground,
    backgroundColor,
    className,
    'data-testid': dataTestId,
  }: DynamicIconProps) => {
    const { iconSource, LazyIcon } = useMemo(() => {
      const [folder, iconSource] = Object.entries(iconMapByFolder)
        .map(([folder, map]) => (code ? [folder, map[code]] : [null, null]))
        .find(([_, source]) => !!source) || [null, null];

      const LazyIcon = code && iconSource && folder ? getLazyIcon(folder, iconSource) : null;

      return { folder, iconSource, LazyIcon };
    }, [code]);

    const defaultTestId = useMemo(
      () => `DynamicIcon${TypographyService.pascalCase(iconSource) || 'DefaultDynamicIcon'}`,
      [iconSource],
    );

    if (code === 'NO_ICON') {
      return null;
    }

    return (
      <StyledDynamicIconBox
        fill={fill}
        color={color}
        stroke={stroke}
        sx={sx}
        className={className}
        onClick={onClick}
        withBackground={withBackground}
        backgroundColor={backgroundColor}
        data-testid={dataTestId || defaultTestId}>
        <Suspense fallback={<DynamicIconSkeleton width={width} height={height} />}>
          {LazyIcon ? <LazyIcon style={{ width, height, fill, stroke }} /> : <DefaultIcon style={{ width, height }} />}
        </Suspense>
      </StyledDynamicIconBox>
    );
  },
  (
    { sx: prevPropsSx, onClick: prevPropsOnClick, ...prevProps },
    { sx: nextPropsSx, onClick: nextPropsOnClick, ...nextProps },
  ) => {
    let propsAreEqual = true;

    if (prevPropsOnClick !== nextPropsOnClick) {
      propsAreEqual = false;
    }

    if (JSON.stringify(prevPropsSx) !== JSON.stringify(nextPropsSx)) {
      propsAreEqual = false;
    }

    if (JSON.stringify(prevProps) !== JSON.stringify(nextProps)) {
      propsAreEqual = false;
    }

    return propsAreEqual;
  },
);
DynamicIcon.displayName = 'DynamicIcon';
